/**
 * `useGraphViewStore` — the Graph surface's ephemeral view state (frontend.md §2.2).
 *
 * The focal note and the radius are in the URL (`/graph/:focalNoteId?radius=`) — they are
 * shareable/restorable. What lives here is the transient interaction state: the active
 * edge-type filter set and the current inspector target (the node or edge the user
 * clicked). A refresh resets those; that is intentional.
 */

import { create } from 'zustand';
import type { EdgeType } from '@/types';
import { EDGE_TYPES } from '@/types';

/** What the `<NodeInspector>` / `<EdgeInspector>` is pointed at. */
export interface InspectorTarget {
  kind: 'node' | 'edge';
  /** A NoteId for a node; a synthetic `from→to|type` key for an edge. */
  id: string;
}

interface GraphViewState {
  /** The edge types currently shown — defaults to all nine (D-005 two-class filter UI). */
  activeEdgeTypes: EdgeType[];
  inspectorTarget: InspectorTarget | null;
  setActiveEdgeTypes: (types: EdgeType[]) => void;
  toggleEdgeType: (type: EdgeType) => void;
  setInspectorTarget: (target: InspectorTarget | null) => void;
}

export const useGraphViewStore = create<GraphViewState>((set) => ({
  activeEdgeTypes: [...EDGE_TYPES],
  inspectorTarget: null,
  setActiveEdgeTypes: (activeEdgeTypes) => set({ activeEdgeTypes }),
  toggleEdgeType: (type) =>
    set((s) => ({
      activeEdgeTypes: s.activeEdgeTypes.includes(type)
        ? s.activeEdgeTypes.filter((t) => t !== type)
        : [...s.activeEdgeTypes, type],
    })),
  setInspectorTarget: (inspectorTarget) => set({ inspectorTarget }),
}));
